import { useCallback, useEffect, useState } from "react";
import type { MutableRefObject } from "react";

import {
    getKeyboardFocusableElements,
    isSelectedElement,
    isSelectedElementByFlag,
} from "../utils/dom/dom";
import { KEY_CODE } from "../utils/dom/keyCodeMapping";

export enum NavigationType {
    TAB = "tab",
    ARROWS = "arrows",
}
export enum OrientationType {
    HORIZONTAL = "horizontal",
    VERTICAL = "vertical",
}

interface IOptions {
    /** Если компонент находится в дереве всегда, нам нужно вызвать ререндер хука. Основываться будем не факте видимости компонента  */
    isOpen?: boolean;
    /** Выбираем тип навигации: табом или по стрелочкам */
    navigation?: NavigationType;
    /** Выбираем ось, по которой будем навигироваться: по-горзинонтали или по-вертикали */
    orientation?: OrientationType;
    /** Надо ли сфокусироваться на элементе, с которого было открыто окно? */
    focusToPrevElementAfterUnmount?: boolean;
    /** Дополнительный обработчик клавиатуры */
    onKeyDown?: (e: KeyboardEvent) => void;
    /**
     * Этот хук является вложенным в другой такой же хук, тогда всё управление навигацией передаётся хуку-родителю,
     * инициализация не осуществляется, но устанавливается слушатель, если передали onKeyDown.
     */
    isSubHook?: boolean;
    /** Надо ли сфокусироваться на активном элементе после маунта? */
    withAutoFocus?: boolean;
}

const defaultOptions = {
    /** По-умолчанию считаем, что хук отрабатывает в открытом компоненте */
    isOpen: true,
    navigation: NavigationType.TAB,
    orientation: OrientationType.HORIZONTAL,
    focusToPrevElementAfterUnmount: true,
    isSubHook: false,
    withAutoFocus: true,
};

const getDataByElement = (el: HTMLElement) => {
    const focusableElements = getKeyboardFocusableElements(el);
    const activeElement = document.activeElement as HTMLElement;
    const activeElementIndex = focusableElements.indexOf(activeElement);
    const firstElement = focusableElements[0] || null;
    const lastElement = focusableElements[focusableElements.length - 1] || null;

    return {
        activeElement,
        focusableElements,
        activeElementIndex,
        firstElement,
        lastElement,
    };
};

/**
 * Замыкает навигацию по элементам внутри элемента-контейнера (см. использование в Dropdown.Menu или Tabs).
 *
 * @author alexander.gordeev (alexander.gordeev@vk.team)
 */
export const useFocusTrap = (
    el: MutableRefObject<HTMLElement | null>,
    options: IOptions = defaultOptions,
) => {
    const {
        isOpen,
        orientation,
        navigation,
        withAutoFocus,
        focusToPrevElementAfterUnmount,
        onKeyDown,
        isSubHook,
    } = {
        ...defaultOptions,
        ...options,
    };

    const [isMounted, setIsMounted] = useState(false);

    /**
     * Перейти к следующему элементу
     */
    const focusToNextElement = useCallback(() => {
        const {
            activeElement,
            focusableElements,
            activeElementIndex,
            lastElement,
            firstElement,
        } = getDataByElement(el.current!);
        let nextEl;
        if (activeElement?.isEqualNode(lastElement) && firstElement) {
            nextEl = firstElement;
        } else {
            nextEl = focusableElements[activeElementIndex + 1];
        }
        nextEl.focus();
    }, [el]);

    /**
     * Перейти к предыдущему элементу
     */
    const focusToPrevElement = useCallback(() => {
        const {
            activeElement,
            focusableElements,
            activeElementIndex,
            lastElement,
            firstElement,
        } = getDataByElement(el.current!);
        let prevEl;
        if (activeElement?.isEqualNode(firstElement) && lastElement) {
            prevEl = lastElement;
        } else {
            prevEl = focusableElements[activeElementIndex - 1];
        }
        prevEl.focus();
    }, [el]);

    /**
     * Общая функция для навигации к следующему/предыдующему элементу.
     */
    const focusTo = useCallback(
        (needPrev: boolean) => {
            if (needPrev) {
                focusToPrevElement();
            } else {
                focusToNextElement();
            }
        },
        [focusToNextElement, focusToPrevElement],
    );

    /**
     * Навигация с помощью таба
     */
    const tabPressHandler = useCallback(
        (e: KeyboardEvent) => {
            if (e.key !== KEY_CODE.TAB) {
                return;
            }
            const { activeElement, firstElement, lastElement } =
                getDataByElement(el.current!);

            /**
             * Используем нативную навигацию табом. Меняем поведение только на первом и последнем элементах
             */
            if (e.shiftKey) {
                if (activeElement?.isEqualNode(firstElement) && lastElement) {
                    lastElement.focus();
                    e.preventDefault();
                }
            } else if (
                activeElement?.isEqualNode(lastElement) &&
                firstElement
            ) {
                firstElement.focus();
                e.preventDefault();
            }
        },
        [el],
    );

    /**
     * Навигация с помощью стрелок
     */
    const arrowPressHandler = useCallback(
        (e: KeyboardEvent) => {
            switch (orientation) {
                case OrientationType.VERTICAL: {
                    if (
                        e.key !== KEY_CODE.ARROW_UP &&
                        e.key !== KEY_CODE.ARROW_DOWN
                    ) {
                        return;
                    }

                    focusTo(e.key === KEY_CODE.ARROW_UP);
                    break;
                }
                case OrientationType.HORIZONTAL: {
                    if (
                        e.key !== KEY_CODE.ARROW_LEFT &&
                        e.key !== KEY_CODE.ARROW_RIGHT
                    ) {
                        return;
                    }

                    focusTo(e.key === KEY_CODE.ARROW_LEFT);
                    break;
                }
                default:
                    break;
            }
            e.preventDefault();
        },
        [focusTo, orientation],
    );

    /**
     * Общий слушатель клавиатуры
     */
    const keyDownHandler = useCallback(
        (e: KeyboardEvent) => {
            switch (navigation) {
                case NavigationType.TAB:
                    tabPressHandler(e);
                    break;
                case NavigationType.ARROWS:
                    arrowPressHandler(e);
                    break;
                default:
                    break;
            }
            onKeyDown?.(e);
        },
        [navigation, onKeyDown, tabPressHandler, arrowPressHandler],
    );

    /**
     * Сохраняем элемент, с которого открыли окно.
     */
    useEffect(() => {
        if (!isMounted || !focusToPrevElementAfterUnmount) {
            return;
        }

        const prevElement = document.activeElement as HTMLElement;
        if (!prevElement) {
            return;
        }

        return () => {
            /**
             * После выхода из модального окна - возвращаемся туда, откуда открыли окно
             */
            prevElement.focus();
        };
    }, [focusToPrevElementAfterUnmount, isMounted]);

    /**
     * Фокусируемся на активный элемент.
     * Если это вложенный хук, то игнорируем
     */
    useEffect(() => {
        const { current } = el;
        if (!isMounted || isSubHook || !current || !withAutoFocus) {
            return;
        }
        const { focusableElements, firstElement } = getDataByElement(current);
        if (!focusableElements.length) {
            return;
        }

        /**
         * Ищем элемент, на который надо сфокусироваться.
         * В приоритете будут элементы с флагом data-is-active
         */
        const focusableElement =
            focusableElements.find(isSelectedElementByFlag) ||
            focusableElements.find(isSelectedElement) ||
            firstElement;

        /**
         * После получения элемента - фокусируемся на него
         */
        focusableElement?.focus();
    }, [isSubHook, el, withAutoFocus, isMounted]);

    /**
     * Устанавливаем слушатели
     */
    useEffect(() => {
        const { current } = el;
        if (!isMounted || !current) {
            return;
        }

        if (isSubHook) {
            if (onKeyDown) {
                current.addEventListener("keydown", onKeyDown);
                return () => {
                    current.removeEventListener("keydown", onKeyDown);
                };
            }
        } else {
            current.addEventListener("keydown", keyDownHandler);
            return () => {
                current.removeEventListener("keydown", keyDownHandler);
            };
        }
    }, [el, keyDownHandler, isSubHook, onKeyDown, isMounted]);

    /**
     * Начинаем работу только после маунта компонента
     */
    useEffect(() => {
        setIsMounted(isOpen);
        return () => setIsMounted(false);
    }, [isOpen]);
};
